"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.QueueNumberPolicy = exports.QueueDisplaySnapshot = exports.QueueViewingDistance = exports.KitchenAction = exports.KitchenTicket = exports.KitchenTicketLine = exports.KitchenStation = exports.FULFILMENT_TRANSITIONS = void 0;
exports.isLegalFulfilmentTransition = isLegalFulfilmentTransition;
const zod_1 = require("zod");
const self_order_js_1 = require("./self-order.js");
/**
 * Preparation routing and the two operational read models
 * (self-order spec v2.3 §6.4, §12.2, §12.3).
 *
 * Neither projection owns truth. The kitchen board and the queue display are
 * rebuilt from the durable order and event model; a staff action updates that
 * model first and the display follows. Kitchen or queue state held only in a UI
 * is state that disappears on refresh and disagrees with the receipt.
 */
/* -------------------------------------------------------------------------- */
/* Transitions                                                                */
/* -------------------------------------------------------------------------- */
/**
 * The only legal fulfilment moves.
 *
 * Declared as data rather than buried in a service `switch`, so the terminal,
 * the platform and the tests agree on one table. `no_show` is reachable only
 * from `ready`: an order nobody collected must have been collectable.
 */
exports.FULFILMENT_TRANSITIONS = Object.freeze({
    not_released: ['queued', 'cancelled'],
    queued: ['preparing', 'cancelled'],
    preparing: ['ready', 'cancelled'],
    ready: ['collected', 'no_show', 'cancelled'],
    collected: [],
    cancelled: [],
    no_show: [],
});
function isLegalFulfilmentTransition(from, to) {
    const allowed = exports.FULFILMENT_TRANSITIONS[from];
    return allowed !== undefined && allowed.includes(to);
}
/* -------------------------------------------------------------------------- */
/* Kitchen                                                                    */
/* -------------------------------------------------------------------------- */
exports.KitchenStation = zod_1.z.object({
    id: zod_1.z.string().min(1),
    locationId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1).max(120),
    /** Items whose preparation tags intersect these are routed here. */
    preparationTags: zod_1.z.array(zod_1.z.string().min(1).max(64)).default([]),
    /** Seconds after release at which a ticket is rendered as overdue. */
    overdueAfterSeconds: zod_1.z.number().int().min(30).max(7200).nullable().default(null),
    isEnabled: zod_1.z.boolean().default(true),
    sortOrder: zod_1.z.number().int().min(0).default(0),
});
exports.KitchenTicketLine = zod_1.z.object({
    lineId: zod_1.z.string().min(1),
    displayName: zod_1.z.string().min(1).max(512),
    quantity: zod_1.z.number().int().min(1),
    /** Rendered under the line; already resolved from the chosen modifiers. */
    preparationNotes: zod_1.z.string().max(1024).optional(),
    modifierLabels: zod_1.z.array(zod_1.z.string().min(1).max(512)).default([]),
});
/**
 * One order as the kitchen sees it.
 *
 * `paymentStatus` is carried deliberately. "Ready" does not imply "paid" (§6.4),
 * and a board that hides the payment fact invites handing over an unpaid order.
 */
exports.KitchenTicket = zod_1.z.object({
    orderId: zod_1.z.string().min(1),
    displayNumber: zod_1.z.string().min(1).max(16),
    stationId: zod_1.z.string().min(1).nullable().default(null),
    fulfilmentStatus: self_order_js_1.SelfOrderFulfilmentStatus,
    paymentStatus: self_order_js_1.SelfOrderPaymentStatus,
    orderType: zod_1.z.enum(['eat_in', 'takeaway']),
    releasedAt: zod_1.z.string().datetime().nullable().default(null),
    startedAt: zod_1.z.string().datetime().nullable().default(null),
    readyAt: zod_1.z.string().datetime().nullable().default(null),
    isOverdue: zod_1.z.boolean().default(false),
    lines: zod_1.z.array(exports.KitchenTicketLine).min(1),
    /** Optimistic concurrency: a kitchen action states the version it saw. */
    orderVersion: zod_1.z.number().int().min(0),
});
exports.KitchenAction = zod_1.z.enum(['start', 'ready', 'recall']);
/* -------------------------------------------------------------------------- */
/* Queue display                                                              */
/* -------------------------------------------------------------------------- */
/**
 * How far the display is read from, biasing the type scale one step either way
 * (Amendment 1, D6). Validated against the display's resolution at configuration
 * time, not here.
 */
exports.QueueViewingDistance = zod_1.z.enum(['near', 'standard', 'far']);
/**
 * The public display payload.
 *
 * Anonymous numbers only. There is deliberately no customer name, no line
 * content, no amount and no order id — this renders on a screen in a public room
 * and every field added here is a field a stranger reads. The `+N more` collapse
 * exists so the display never scrolls and never truncates silently.
 */
exports.QueueDisplaySnapshot = zod_1.z.object({
    locationId: zod_1.z.string().min(1),
    generatedAt: zod_1.z.string().datetime(),
    preparing: zod_1.z.array(zod_1.z.string().min(1).max(16)),
    ready: zod_1.z.array(zod_1.z.string().min(1).max(16)),
    /** Preparing entries not shown because the smallest type step still overflowed. */
    additionalPreparingCount: zod_1.z.number().int().min(0).default(0),
    viewingDistance: exports.QueueViewingDistance.default('standard'),
});
/**
 * Queue numbering policy.
 *
 * The reset rule matters more than it looks: a number reused while the previous
 * holder is still waiting sends the wrong customer to the counter.
 */
exports.QueueNumberPolicy = zod_1.z.object({
    locationId: zod_1.z.string().min(1),
    /** Printf-style, e.g. `A%03d`. Validated on save, not here. */
    format: zod_1.z.string().min(1).max(32),
    rangeStart: zod_1.z.number().int().min(0),
    rangeEnd: zod_1.z.number().int().min(1),
    resetDaily: zod_1.z.boolean().default(true),
    /** How long a Ready number stays visible after collection. */
    readyVisibleSeconds: zod_1.z.number().int().min(5).max(3600).default(120),
    /** Audio cue on first transition into Ready. */
    announceOnReady: zod_1.z.boolean().default(true),
});
//# sourceMappingURL=fulfilment.js.map